import { useEffect, useMemo, useState } from 'react';
import {
  makeStyles, tokens, Title1, Title3, Text, Textarea, Button, Spinner,
  Badge, Checkbox, Divider, ProgressBar, Dropdown, Option, Field,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
} from '@fluentui/react-components';
import { apiGet, apiPost } from '../api/client';
import type {
  ProviderDescriptor, BenchmarkRunRequest, BenchmarkRunResponse,
  BenchmarkProviderResult,
  GoldenDatasetSummary, GoldenDatasetDetail, SignalCareRole,
} from '../api/types';
import { PipelineTimeline } from '../components/PipelineTimeline';
import { ExecutiveRecommendation } from '../components/ExecutiveRecommendation';
import { ScoreBarChart } from '../components/ScoreBarChart';
import { CostQualityScatter } from '../components/CostQualityScatter';
import type { ExecSeriesPoint } from '../components/execTypes';
import { listDatasets, getDataset } from '../api/datasets';
import { listRoles } from '../api/prompts';

const ROLE_LABEL: Record<SignalCareRole, string> = {
  MemberIntake: 'Member Intake',
  CareCoordinator: 'Care Coordinator',
  Lpn: 'LPN',
  Rn: 'RN',
  Doctor: 'Doctor',
  Billing: 'Billing',
  Claims: 'Claims',
};

/**
 * Benchmark Lab — fan out ONE question to N providers, rank the results.
 *
 * This is the "provider abstraction pays off" page: the user picks
 * providers, hits Run, and sees side-by-side answer quality + cost + latency
 * for each. The ranking + recommendation panel translates that into a
 * business call the exec-audience of the demo can act on.
 */

// Griffel (Fluent's CSS-in-JS engine used by makeStyles) requires string CSS
// values with units — unlike React's inline `style={{}}` which accepts numbers.
// All numeric CSS below is written as '16px', not 16.
const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '16px' },
  section: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '20px 24px',
  },
  configRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: '12px',
    marginTop: '12px',
  },
  providerCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '10px 12px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '6px',
    background: tokens.colorNeutralBackground2,
  },
  providerHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' },
  runControls: { display: 'flex', gap: '12px', alignItems: 'center', marginTop: '16px', flexWrap: 'wrap' },

  chartsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
    gap: '16px',
  },

  resultsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
    gap: '16px',
  },
  resultCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '16px 20px',
    minHeight: '320px',
  },
  resultHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '8px',
  },
  answerBox: {
    whiteSpace: 'pre-wrap',
    fontSize: '14px',
    lineHeight: '1.55',
    background: tokens.colorNeutralBackground2,
    borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
    borderRadius: '4px',
    padding: '10px 12px',
    maxHeight: '260px',
    overflow: 'auto',
  },
  metricRow: { display: 'flex', justifyContent: 'space-between', fontSize: '13px' },
  scoreGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginTop: '6px' },
  scoreBar: { display: 'flex', flexDirection: 'column', gap: '4px' },
  scoreLabel: { display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: tokens.colorNeutralForeground2 },

  rankTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '12px',
    fontSize: '13px',
  },
  th: {
    textAlign: 'left',
    padding: '8px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2,
    fontWeight: '600',
  },
  td: {
    padding: '10px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  },
});

function scoreColor(v: number): 'success' | 'warning' | 'danger' {
  if (v >= 0.75) return 'success';
  if (v >= 0.5) return 'warning';
  return 'danger';
}

function providerBadgeColor(id: string): 'brand' | 'informative' | 'success' | 'important' | 'severe' {
  switch (id) {
    case 'azure-openai':    return 'brand';
    case 'openai':          return 'success';
    case 'anthropic':       return 'important';
    case 'azure-ai-foundry':return 'severe';
    default:                return 'informative';
  }
}

export default function BenchmarkLab() {
  const s = useStyles();
  const [providers, setProviders] = useState<ProviderDescriptor[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [question, setQuestion] = useState('What is the medication administration verification protocol?');
  const [useRag, setUseRag] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<BenchmarkRunResponse | null>(null);
  const [showDatasetPicker, setShowDatasetPicker] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const list = await apiGet<ProviderDescriptor[]>('/api/providers');
        setProviders(list);
        // Preselect every configured provider so a fresh user sees N-way comparison
        setSelected(new Set(list.filter(p => p.isConfigured).map(p => p.providerId)));
      } catch (e) {
        setErr(`Could not load providers: ${(e as Error).message}`);
      }
    })();
  }, []);

  const configuredCount = useMemo(
    () => providers.filter(p => p.isConfigured).length,
    [providers]);

  function toggle(providerId: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(providerId)) next.delete(providerId); else next.add(providerId);
      return next;
    });
  }

  async function run() {
    setErr(null);
    setBusy(true);
    setResult(null);
    try {
      const req: BenchmarkRunRequest = {
        question,
        useRagContext: useRag,
        providerIds: Array.from(selected),
      };
      const r = await apiPost<BenchmarkRunResponse>('/api/benchmarks/run', req);
      setResult(r);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const canRun = !busy && question.trim().length > 0 && selected.size > 0;

  // Derive ExecSeriesPoint[] for executive-view components.
  const points = useMemo<ExecSeriesPoint[]>(() =>
    result?.providerResults.map(r => ({
      key: r.providerId,
      label: r.providerName,
      overallScore: r.overallScore,
      costUsd: r.estimatedCostUsd,
      latencyMs: r.latencyMs,
      hallucinationRisk: r.hallucinationRisk,
      isError: r.isError,
      errorText: r.error ?? undefined,
    })) ?? [],
  [result]);

  // Order results by ranking (winner first). Errored providers land at bottom.
  const orderedResults = useMemo(() => {
    if (!result) return [];
    const byId = new Map(result.providerResults.map(r => [r.providerId, r]));
    const good = result.ranking.map(id => byId.get(id)).filter(Boolean) as BenchmarkProviderResult[];
    const rest = result.providerResults.filter(r => !result.ranking.includes(r.providerId));
    return [...good, ...rest];
  }, [result]);

  return (
    <div className={s.page}>
      <div>
        <Title1 as="h1">Benchmark Lab</Title1>
        <Text size={200} block style={{ color: tokens.colorNeutralForeground2 }}>
          Fan out one question to every configured provider. Same context. Same prompt. Ranked answer, cost, and latency.
        </Text>
      </div>

      {/* -------- 1) Question + RAG toggle -------- */}
      <div className={s.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Title3 as="h2">Question</Title3>
          <Button size="small" onClick={() => setShowDatasetPicker(true)}>Load from dataset</Button>
        </div>
        <Textarea
          value={question}
          onChange={(_, d) => setQuestion(d.value)}
          rows={3}
          style={{ width: '100%', marginTop: 8 }}
        />
        <div style={{ marginTop: 12 }}>
          <Checkbox
            checked={useRag}
            onChange={(_, d) => setUseRag(!!d.checked)}
            label="Use RAG context (retrieve from uploaded documents)"
          />
        </div>
      </div>

      {/* -------- 2) Provider selector -------- */}
      <div className={s.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Title3 as="h2">Providers</Title3>
          <Badge color={configuredCount > 0 ? 'success' : 'danger'} appearance="tint">
            {configuredCount} configured
          </Badge>
        </div>
        <Text size={200} block style={{ color: tokens.colorNeutralForeground2, marginTop: 4 }}>
          Uncheck any provider to skip it. Unconfigured providers are shown but disabled.
        </Text>
        <div className={s.configRow}>
          {providers.map(p => (
            <div key={p.providerId} className={s.providerCard}
              style={{ opacity: p.isConfigured ? 1 : 0.55 }}>
              <div className={s.providerHeader}>
                <Checkbox
                  checked={selected.has(p.providerId)}
                  onChange={() => toggle(p.providerId)}
                  disabled={!p.isConfigured}
                  label={
                    <span style={{ fontWeight: 600 }}>
                      {p.displayName}
                    </span>
                  }
                />
                <Badge color={providerBadgeColor(p.providerId)} appearance="tint">
                  {p.providerId}
                </Badge>
              </div>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                Model: <strong>{p.modelName}</strong>
              </Text>
              <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
                {p.isConfigured ? p.notes : 'Not configured — set the corresponding Providers:* section.'}
              </Text>
            </div>
          ))}
        </div>
        <div className={s.runControls}>
          <Button appearance="primary" size="large" onClick={run} disabled={!canRun}>
            {busy ? 'Running…' : `Run Benchmark (${selected.size})`}
          </Button>
          {busy && <Spinner size="tiny" label="Fanning out to providers…" />}
          {err && <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{err}</Text>}
        </div>
      </div>

      {/* -------- 3) Recommendation -------- */}
      {result && (
        <ExecutiveRecommendation
          points={points}
          serverRecommendation={result.recommendation}
          entityLabel="providers"
        />
      )}

      {/* -------- 3a) Charts -------- */}
      {result && (
        <div className={s.section}>
          <div className={s.chartsGrid}>
            <ScoreBarChart points={points} title="Overall score by provider" />
            <CostQualityScatter points={points} title="Cost vs quality" qualityFloor={0.6} />
          </div>
        </div>
      )}

      {/* -------- 4) Ranking table -------- */}
      {result && (
        <div className={s.section}>
          <Title3 as="h2">Ranking</Title3>
          <table className={s.rankTable}>
            <thead>
              <tr>
                <th className={s.th}>#</th>
                <th className={s.th}>Provider</th>
                <th className={s.th}>Model</th>
                <th className={s.th} style={{ textAlign: 'right' }}>Overall</th>
                <th className={s.th} style={{ textAlign: 'right' }}>Cost</th>
                <th className={s.th} style={{ textAlign: 'right' }}>Latency</th>
                <th className={s.th} style={{ textAlign: 'right' }}>Hallucination risk</th>
                <th className={s.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {orderedResults.map((r, i) => (
                <tr key={r.providerId}>
                  <td className={s.td}>{r.isError ? '—' : i + 1}</td>
                  <td className={s.td}>
                    <Badge color={providerBadgeColor(r.providerId)} appearance="tint">
                      {r.providerName}
                    </Badge>
                  </td>
                  <td className={s.td}>{r.modelName}</td>
                  <td className={s.td} style={{ textAlign: 'right' }}>
                    {r.isError ? '—' : (
                      <Badge color={scoreColor(r.overallScore)}>{(r.overallScore * 100).toFixed(0)}</Badge>
                    )}
                  </td>
                  <td className={s.td} style={{ textAlign: 'right' }}>
                    ${r.estimatedCostUsd.toFixed(5)}
                  </td>
                  <td className={s.td} style={{ textAlign: 'right' }}>
                    {r.latencyMs.toFixed(0)} ms
                  </td>
                  <td className={s.td} style={{ textAlign: 'right' }}>
                    {r.isError ? '—' : `${(r.hallucinationRisk * 100).toFixed(0)}%`}
                  </td>
                  <td className={s.td}>
                    {r.isError
                      ? <Badge color="danger" appearance="tint">error</Badge>
                      : <Badge color="success" appearance="tint">ok</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* -------- 4a) Pipeline timeline -------- */}
      {result && (
        <div className={s.section}>
          <Title3 as="h2" style={{ marginBottom: 12 }}>Pipeline Timeline</Title3>
          <Text size={200} block style={{ color: tokens.colorNeutralForeground3, marginBottom: 12 }}>
            Every observable step this run took — retrieval, provider calls, evaluation, ranking.
          </Text>
          <PipelineTimeline runType="benchmark" runId={result.runId} />
        </div>
      )}

      {/* -------- 5) Side-by-side result cards -------- */}
      {result && (
        <div className={s.resultsGrid}>
          {orderedResults.map((r, i) => (
            <div key={r.providerId} className={s.resultCard}
              style={i === 0 ? { boxShadow: `0 0 0 2px ${tokens.colorBrandStroke1}` } : {}}>
              <div className={s.resultHeader}>
                <div>
                  <Text weight="semibold" size={400} block>
                    {r.providerName} <Badge color={providerBadgeColor(r.providerId)} appearance="tint">#{i + 1}</Badge>
                  </Text>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{r.modelName}</Text>
                </div>
                {r.isError
                  ? <Badge color="danger">error</Badge>
                  : <Badge color={scoreColor(r.overallScore)}>{(r.overallScore * 100).toFixed(0)}</Badge>}
              </div>

              {r.isError ? (
                <Text style={{ color: tokens.colorPaletteRedForeground1 }}>
                  {r.error ?? 'Unknown error'}
                </Text>
              ) : (
                <>
                  <div className={s.answerBox}>{r.answer}</div>
                  <Divider />
                  <div className={s.scoreGrid}>
                    {[
                      { label: 'Groundedness', v: r.groundednessScore },
                      { label: 'Relevance',    v: r.relevanceScore },
                      { label: 'Citation',     v: r.citationScore },
                      { label: 'Safety',       v: r.safetyScore },
                    ].map(m => (
                      <div key={m.label} className={s.scoreBar}>
                        <div className={s.scoreLabel}>
                          <span>{m.label}</span><span>{(m.v * 100).toFixed(0)}</span>
                        </div>
                        <ProgressBar value={m.v} thickness="medium" />
                      </div>
                    ))}
                  </div>
                  <Divider />
                  <div className={s.metricRow}>
                    <span>Input / output tokens</span>
                    <span>{r.inputTokens.toLocaleString()} / {r.outputTokens.toLocaleString()}</span>
                  </div>
                  <div className={s.metricRow}>
                    <span>Estimated cost</span>
                    <span>${r.estimatedCostUsd.toFixed(5)}</span>
                  </div>
                  <div className={s.metricRow}>
                    <span>Latency</span>
                    <span>{r.latencyMs.toFixed(0)} ms</span>
                  </div>
                  <div className={s.metricRow}>
                    <span>Citations found</span>
                    <span>{r.citations.length}</span>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <DatasetQuestionPickerDialog
        open={showDatasetPicker}
        onClose={() => setShowDatasetPicker(false)}
        onPicked={(text) => { setQuestion(text); setShowDatasetPicker(false); }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dataset → question picker dialog
//
// Non-invasive addition: the existing single-question run flow stays intact.
// Picking a question just fills the Question textarea with its text. The
// matrix-across-a-full-dataset flow is M3's job.
// ---------------------------------------------------------------------------
function DatasetQuestionPickerDialog(props: {
  open: boolean;
  onClose: () => void;
  onPicked: (questionText: string) => void;
}) {
  const [roles, setRoles] = useState<SignalCareRole[]>([]);
  const [role, setRole] = useState<SignalCareRole | 'ALL'>('ALL');
  const [datasets, setDatasets] = useState<GoldenDatasetSummary[]>([]);
  const [selectedDsId, setSelectedDsId] = useState<string | null>(null);
  const [detail, setDetail] = useState<GoldenDatasetDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!props.open) return;
    (async () => {
      try { setRoles(await listRoles()); }
      catch (e) { setErr((e as Error).message); }
    })();
  }, [props.open]);

  useEffect(() => {
    if (!props.open) return;
    (async () => {
      try {
        const items = await listDatasets(role === 'ALL' ? undefined : role);
        setDatasets(items);
        setSelectedDsId(items[0]?.id ?? null);
      } catch (e) { setErr((e as Error).message); }
    })();
  }, [props.open, role]);

  useEffect(() => {
    if (!selectedDsId) { setDetail(null); return; }
    (async () => {
      try { setDetail(await getDataset(selectedDsId)); }
      catch (e) { setErr((e as Error).message); }
    })();
  }, [selectedDsId]);

  return (
    <Dialog open={props.open} onOpenChange={(_, d) => !d.open && props.onClose()}>
      <DialogSurface style={{ maxWidth: 720 }}>
        <DialogBody>
          <DialogTitle>Load question from golden dataset</DialogTitle>
          <DialogContent>
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Role">
                  <Dropdown
                    value={role === 'ALL' ? 'All roles' : ROLE_LABEL[role]}
                    selectedOptions={[role]}
                    onOptionSelect={(_e, data) => data.optionValue && setRole(data.optionValue as SignalCareRole | 'ALL')}
                  >
                    <Option value="ALL">All roles</Option>
                    {roles.map((r) => (
                      <Option key={r} value={r}>{ROLE_LABEL[r]}</Option>
                    ))}
                  </Dropdown>
                </Field>
                <Field label="Dataset">
                  <Dropdown
                    value={datasets.find((d) => d.id === selectedDsId)?.name ?? '—'}
                    selectedOptions={selectedDsId ? [selectedDsId] : []}
                    onOptionSelect={(_e, data) => data.optionValue && setSelectedDsId(data.optionValue)}
                  >
                    {datasets.map((d) => (
                      <Option key={d.id} value={d.id} text={`${d.name} (${d.questionCount})`}>
                        {d.name} ({d.questionCount})
                      </Option>
                    ))}
                  </Dropdown>
                </Field>
              </div>

              {err && <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{err}</Text>}

              <div>
                <Text weight="semibold" size={200}>Questions</Text>
                {!detail && <Spinner size="tiny" style={{ marginTop: 8 }} />}
                {detail?.questions.length === 0 && <Text size={200}>This dataset is empty.</Text>}
                <div style={{ display: 'grid', gap: 8, marginTop: 8, maxHeight: 320, overflow: 'auto' }}>
                  {detail?.questions.map((q) => (
                    <div
                      key={q.id}
                      style={{
                        padding: '10px 12px',
                        border: `1px solid ${tokens.colorNeutralStroke2}`,
                        borderRadius: 6,
                        background: tokens.colorNeutralBackground2,
                        cursor: 'pointer',
                      }}
                      onClick={() => props.onPicked(q.question)}
                    >
                      <Text size={200} weight="semibold">Q{q.orderIndex}</Text>
                      <Text size={200} block>{q.question}</Text>
                      {q.expectedAnswerKeywords.length > 0 && (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                          {q.expectedAnswerKeywords.map((k) => (
                            <Badge key={k} appearance="outline" size="small">{k}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={props.onClose}>Cancel</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
